import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Video, Flame, Home as HomeIcon, Lock, MapPin, ArrowRight, Phone, CheckCircle, Cpu, Zap, Volume2, Smartphone, Wifi, Bell, Eye, Clock, Shield, Award, Users, Wrench, HeartHandshake, Star, Play, ChevronRight, ChevronLeft, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import siteConfig from '../config/siteConfig';
import useEmblaCarousel from 'embla-carousel-react';
import { AnimatedSection } from '../hooks/useScrollAnimation';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef(null);
  const appVideoRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/products?category=basic`);
        if (response.ok) {
          const data = await response.json();
          setBestSellers(data.slice(0, 4));
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const enableSound = () => {
      if (videoRef.current && !soundEnabled) {
        videoRef.current.muted = false;
        videoRef.current.play().catch(() => {});
        setSoundEnabled(true);
      }
    };

    document.addEventListener('click', enableSound, { once: true });
    return () => document.removeEventListener('click', enableSound);
  }, [soundEnabled]);

  const systemAdvantages = [
    { icon: Wifi, title: 'Sans fil & fiable', desc: 'Protocole Jeweller avec portée jusqu\'à 2000m et anti-brouillage', color: 'from-green-500 to-emerald-600' },
    { icon: Smartphone, title: 'Pilotage à distance', desc: 'Application Ajax intuitive pour contrôler tout depuis votre smartphone', color: 'from-blue-500 to-cyan-600' },
    { icon: Bell, title: 'Alertes instantanées', desc: 'Notifications push en moins de 0.15 seconde sur intrusion', color: 'from-orange-500 to-amber-600' },
    { icon: Eye, title: 'Détection IA', desc: 'Caméras intelligentes qui distinguent humains, animaux et véhicules', color: 'from-purple-500 to-violet-600' },
    { icon: Clock, title: 'Autonomie record', desc: 'Jusqu\'à 7 ans d\'autonomie sur les détecteurs sans fil', color: 'from-teal-500 to-cyan-600' },
    { icon: Shield, title: 'Anti-sabotage', desc: 'Protection contre l\'arrachement, le brouillage et le piratage', color: 'from-red-500 to-rose-600' },
  ];

  // Pourquoi FGS - Fusionné avec Installation
  const whyFGS = [
    { icon: Award, title: 'Garantie 4 ans', desc: 'Exclusivité FGS : 2 ans de plus que la garantie constructeur', highlight: true },
    { icon: Users, title: 'Techniciens certifiés', desc: 'Installateurs formés et certifiés par Ajax Systems' },
    { icon: Wrench, title: 'Installation pro', desc: 'Intervention rapide, configuration complète et formation incluse' },
    { icon: CreditCard, title: 'Paiement en 3x', desc: 'Payez en 3 fois sans frais avec Klarna dès 35€', highlight: true },
    { icon: HeartHandshake, title: 'Conseil personnalisé', desc: 'Étude gratuite de vos besoins de sécurité' },
    { icon: Phone, title: 'SAV réactif', desc: 'Support technique et dépannage rapide' },
  ];

  const appFeatures = [
    'Armement/désarmement en un tap',
    'Historique des événements',
    'Vidéo en direct',
    'Gestion multi-sites',
    'Partage d\'accès famille',
    'Scénarios automatiques',
  ];

  const reviews = [
    { name: 'Pierre M.', location: 'Montpellier', text: 'Installation rapide et soignée. L\'équipe a pris le temps de tout nous expliquer. Système parfait !', avatar: 'PM' },
    { name: 'Sophie L.', location: 'Castelnau-le-Lez', text: 'Très satisfaite de mon système Ajax. L\'application est vraiment intuitive et la garantie 4 ans rassure.', avatar: 'SL' },
    { name: 'Marc D.', location: 'Lattes', text: 'Professionnel et réactif. Le SAV a répondu immédiatement quand j\'ai eu une question. Je recommande.', avatar: 'MD' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>FGS Alarme Montpellier | Alarme Maison & Vidéosurveillance Montpellier - Ajax Systems</title>
        <meta name="description" content="FGS Alarme Montpellier, installateur alarme maison et vidéosurveillance à Montpellier. Alarme sans fil, caméra sans fil, télésurveillance Ajax Systems. Garantie 4 ans, jusqu'à -20% en ligne, TVA 10%. Devis gratuit." />
        <meta name="keywords" content="alarme maison, alarme sans fil, alarme montpellier, installation alarme, caméra sans fil, vidéosurveillance, télésurveillance, alarme ajax, fgs security, alarme maison montpellier, camera surveillance" />
        <link rel="canonical" href="https://maprotection-ia.fr/" />
        <meta property="og:title" content="FGS Alarme Montpellier | Alarme Maison & Vidéosurveillance - Ajax Systems Montpellier" />
        <meta property="og:description" content="Installateur alarme maison et vidéosurveillance Ajax Systems. Alarme sans fil, caméra, télésurveillance. Garantie 4 ans, devis gratuit." />
        <meta property="og:url" content="https://maprotection-ia.fr/" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fb.png" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section - Style Apple Premium */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-[#0a0a0a]" />
        
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-12 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-8">
                <ShieldCheck className="w-4 h-4 text-fgs-primary" />
                <span className="text-sm text-white/70 font-medium">Partenaire officiel Ajax Systems</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-semibold text-white mb-6 leading-[1.05] tracking-tight">
                Sécurisez<br />
                <span className="text-white/70">votre espace</span>
              </h1>
              
              <p className="text-lg md:text-xl text-white/60 mb-10 max-w-lg leading-relaxed font-light">
                Installateur <strong className="text-white/70 font-medium">alarme maison</strong> et 
                <strong className="text-white/70 font-medium"> vidéosurveillance</strong> Ajax Systems à Montpellier.
              </p>

              <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-10">
                <Link to="/shop">
                  <Button size="lg" className="bg-white text-black hover:bg-white/90 h-14 px-8 text-base font-medium rounded-full" data-testid="hero-shop-btn">
                    Boutique alarmes
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white h-14 px-8 text-base hover:bg-white/5 rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Devis gratuit
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-sm">
                {[
                  { text: 'Installation pro', icon: Wrench },
                  { text: 'Garantie 4 ans', icon: Award },
                  { text: 'Jusqu\'à -20%', icon: Shield },
                  { text: '3x sans frais', icon: CreditCard, highlight: true }
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 ${item.highlight ? 'text-[#FFB3C7]' : 'text-white/60'}`}>
                    <item.icon className={`w-4 h-4 ${item.highlight ? 'text-[#FFB3C7]' : 'text-emerald-400'}`} />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative order-first lg:order-last">
              <div className="relative z-10 rounded-2xl md:rounded-3xl overflow-hidden border border-white/10">
                {!videoEnded ? (
                  <>
                    <video 
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      webkit-playsinline="true"
                      className="w-full aspect-video"
                      onEnded={() => setVideoEnded(true)}
                    >
                      <source src="/videos/ajax-protection-ia.mp4" type="video/mp4" />
                    </video>
                    {!soundEnabled && (
                      <button 
                        onClick={() => {
                          if (videoRef.current) {
                            videoRef.current.muted = false;
                            setSoundEnabled(true);
                          }
                        }}
                        className="absolute bottom-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 transition-all text-sm font-medium border border-white/10"
                      >
                        <Volume2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Activer le son</span>
                      </button>
                    )}
                  </>
                ) : (
                  <div className="aspect-video flex flex-col items-center justify-center p-8 bg-[#0a0a0a]">
                    <div className="text-4xl md:text-5xl font-heading font-semibold text-white mb-4 tracking-wider">AJAX</div>
                    <p className="text-white/50 text-sm mb-4">FGS Alarme Montpellier — Installateur certifié</p>
                    <button
                      onClick={() => {
                        setVideoEnded(false);
                        setSoundEnabled(false);
                        if (videoRef.current) {
                          videoRef.current.currentTime = 0;
                          videoRef.current.muted = true;
                          videoRef.current.play();
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white text-sm transition-all"
                    >
                      <Play className="w-4 h-4" />
                      Rejouer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau défilant - Minimaliste */}
      <div className="py-4 bg-[#0a0a0a] border-y border-white/5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 mx-8">
              <span className="text-white/50 font-medium text-sm">Livraison Gratuite dès 250 €</span>
              <span className="text-white/50">•</span>
              <span className="text-white/50 font-medium text-sm">Matériel Garanti 4 Ans</span>
              <span className="text-white/50">•</span>
              <span className="text-white/50 font-medium text-sm">Installation possible</span>
              <span className="text-white/50">•</span>
            </div>
          ))}
        </div>
      </div>

      {/* 1. Protection IA - Style Apple/Tesla Premium */}
      <AnimatedSection>
      <section className="py-20 md:py-32 relative overflow-hidden bg-black">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 relative">
          {/* Header minimaliste style Apple */}
          <div className="text-center mb-12 md:mb-20">
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-[1.1] tracking-tight">
              Un système tout en un :<br />
              <span className="text-white/90">Alarme + Vidéoprotection</span>
            </h2>
            <p className="text-lg md:text-2xl text-white/50 font-light max-w-2xl mx-auto">
              Quand l'IA révolutionne la sécurité.
            </p>
          </div>

          {/* Image principale - Grille IA */}
          <div className="relative mb-16 md:mb-24">
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden">
              <img 
                src="/ai-detection-grid.jpg" 
                alt="Détection IA - Humains, Véhicules, Animaux" 
                className="w-full object-cover"
              />
            </div>
          </div>

          {/* Features en grille minimaliste */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center md:text-left">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Classification IA</h3>
              <p className="text-white/50 leading-relaxed">
                Distingue automatiquement les humains, véhicules et animaux en temps réel.
              </p>
            </div>

            <div className="text-center md:text-left">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Zéro fausse alerte</h3>
              <p className="text-white/50 leading-relaxed">
                Fini les notifications inutiles. Seules les vraies menaces déclenchent une alerte.
              </p>
            </div>

            <div className="text-center md:text-left">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <Video className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Clip vidéo instant</h3>
              <p className="text-white/50 leading-relaxed">
                Recevez un clip de l'intrusion directement sur votre smartphone.
              </p>
            </div>
          </div>

          {/* CTA minimaliste */}
          <div className="text-center mt-12 md:mt-16">
            <Link to="/ai-security">
              <Button size="lg" className="bg-white text-black hover:bg-white/90 h-14 px-10 text-base font-medium rounded-full">
                En savoir plus
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </AnimatedSection>

      {/* 2. Meilleures ventes - Style Premium */}
      <AnimatedSection delay={0.1}>
      <section className="py-20 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="text-white/60 text-sm font-medium tracking-wide uppercase mb-3">Notre sélection</p>
              <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white tracking-tight">
                Meilleures ventes
              </h2>
            </div>
            <Link to="/shop">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black rounded-full px-6 group transition-all">
                Voir la boutique
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white/5 rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {bestSellers.map((product) => (
                <ProductCard key={product.product_id} product={product} compact />
              ))}
            </div>
          )}
        </div>
      </section>
      </AnimatedSection>

      {/* 3. Pourquoi FGS + Installation - Style Apple */}
      <AnimatedSection delay={0.1}>
      <section className="py-20 md:py-32 relative bg-gradient-to-b from-[#0a0a0a] to-[#111]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 relative">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-fgs-primary text-sm font-medium tracking-wide uppercase mb-3">Votre installateur</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
              Pourquoi choisir FGS ?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto text-base md:text-lg font-light">
              Plus qu'un revendeur, votre partenaire sécurité.
            </p>
          </div>

          {/* Grille desktop / Carrousel mobile */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyFGS.map((item, i) => (
              <div 
                key={i}
                className={`relative rounded-2xl p-8 transition-all duration-300 ${
                  item.highlight 
                    ? 'bg-fgs-primary/10 border border-fgs-primary/30' 
                    : 'bg-white/[0.02] border border-white/5 hover:border-white/10'
                }`}
              >
                {item.highlight && (
                  <span className="absolute -top-3 right-6 px-3 py-1 bg-fgs-primary text-white text-xs font-medium rounded-full">
                    Exclusif
                  </span>
                )}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-5 ${item.highlight ? 'bg-fgs-primary/20' : 'bg-white/5'}`}>
                  <item.icon className={`w-6 h-6 ${item.highlight ? 'text-fgs-primary' : 'text-white/70'}`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <WhyFGSCarousel items={whyFGS} />
          </div>

          {/* CTA Installation */}
          <div className="mt-12 md:mt-16 text-center">
            <div className="inline-flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-black hover:bg-white/90 rounded-full h-14 px-8 font-medium">
                  Demander un devis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href={`tel:${siteConfig.contact.phoneFormatted}`}>
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full h-14 px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  {siteConfig.contact.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      </AnimatedSection>

      {/* 4. Application Ajax - Style Apple */}
      <AnimatedSection delay={0.1}>
      <section className="py-20 md:py-32 overflow-hidden bg-[#111]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-white/60 text-sm font-medium tracking-wide uppercase mb-3">Application mobile</p>
              
              <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6 leading-[1.1] tracking-tight">
                Tout contrôler depuis<br />
                <span className="text-white/70">votre smartphone</span>
              </h2>
              
              <p className="text-lg text-white/60 mb-10 leading-relaxed font-light">
                L'application Ajax est votre centre de contrôle. Armez, désarmez, visualisez vos caméras 
                et recevez des alertes instantanées.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-10">
                {appFeatures.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <div className="w-5 h-5 rounded-full bg-fgs-primary/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-fgs-primary" />
                    </div>
                    <span className="text-sm text-white/60">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <a href="https://apps.apple.com/app/ajax-security-system/id1063166247" target="_blank" rel="noopener noreferrer" className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3 transition-all">
                  <div className="text-2xl">🍎</div>
                  <div className="text-left">
                    <div className="text-[10px] text-white/50 uppercase tracking-wider">Télécharger sur</div>
                    <div className="text-white font-medium text-sm">App Store</div>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.ajaxsystems" target="_blank" rel="noopener noreferrer" className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3 transition-all">
                  <div className="text-2xl">▶️</div>
                  <div className="text-left">
                    <div className="text-[10px] text-white/50 uppercase tracking-wider">Disponible sur</div>
                    <div className="text-white font-medium text-sm">Google Play</div>
                  </div>
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl md:rounded-3xl overflow-hidden border border-white/10">
                <video 
                  ref={appVideoRef}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video block"
                >
                  <source src="/videos/ajax-app-control.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>
      </AnimatedSection>

      {/* 5. Pourquoi Ajax Systems - Style Tesla */}
      <AnimatedSection delay={0.1}>
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#111] to-black">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-fgs-primary text-sm font-medium tracking-wide uppercase mb-3">Technologie Ajax</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
              Pourquoi Ajax Systems ?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto text-base md:text-lg font-light">
              Le système de sécurité le plus fiable et le plus vendu en Europe.
            </p>
          </div>

          {/* Grille desktop / Carrousel mobile */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systemAdvantages.map((adv, i) => (
              <div key={i} className="group bg-white/[0.02] border border-white/5 hover:border-white/10 rounded-2xl p-8 text-center transition-all duration-300">
                <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${adv.color} flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <adv.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-2">{adv.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{adv.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <AdvantagesCarousel items={systemAdvantages} />
          </div>
        </div>
      </section>
      </AnimatedSection>

      {/* Avis clients - Style Premium */}
      <AnimatedSection delay={0.1}>
      <section className="py-20 md:py-32 bg-black">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-white/60 text-sm font-medium tracking-wide uppercase mb-3">Témoignages</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6 tracking-tight">
              Ils nous font confiance
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
              </div>
              <span className="text-white/60 text-base ml-2">4.9/5 sur Google</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-8 hover:border-white/10 transition-all">
                <div className="flex gap-0.5 mb-5">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-white/60 text-base mb-6 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fgs-primary to-emerald-500 flex items-center justify-center text-white text-sm font-semibold">
                    {review.avatar}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{review.name}</p>
                    <p className="text-white/50 text-xs">{review.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </AnimatedSection>

      {/* CTA Final - Style Apple */}
      <AnimatedSection delay={0.1}>
      <section className="py-20 md:py-32 bg-black">
        <div className="max-w-[1000px] mx-auto px-4 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Prêt à sécuriser<br />votre espace ?
          </h2>
          <p className="text-lg md:text-xl text-white/60 mb-10 max-w-xl mx-auto font-light">
            Devis gratuit ou jusqu'à -20% sur les produits en ligne.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-black hover:bg-white/90 h-14 px-10 text-base font-medium rounded-full">
                Demander un devis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/shop">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 h-14 px-10 text-base rounded-full">
                Voir la boutique
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </AnimatedSection>
    </div>
  );
};

// Carousel Component for Advantages
const AdvantagesCarousel = ({ items }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi]);

  return (
    <div className="relative">
      <div className="hidden md:flex absolute -top-16 right-0 gap-2">
        <button
          onClick={() => emblaApi?.scrollPrev()}
          disabled={!canScrollPrev}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => emblaApi?.scrollNext()}
          disabled={!canScrollNext}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {items.map((adv, i) => (
            <div key={i} className="flex-shrink-0 w-[280px] md:w-[300px] lg:w-[320px]">
              <div className="group h-full bg-fgs-bg/50 border border-white/5 rounded-2xl p-6 text-center hover:border-fgs-primary/30 transition-all duration-300">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${adv.color} flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <adv.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-3">{adv.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{adv.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

// Carousel Component for Why FGS
const WhyFGSCarousel = ({ items }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi]);

  return (
    <div className="relative">
      <div className="hidden md:flex absolute -top-16 right-0 gap-2">
        <button
          onClick={() => emblaApi?.scrollPrev()}
          disabled={!canScrollPrev}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => emblaApi?.scrollNext()}
          disabled={!canScrollNext}
          className="p-2 rounded-full bg-fgs-surface border border-fgs-border hover:border-fgs-primary/50 disabled:opacity-30 transition-all"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {items.map((item, i) => (
            <div key={i} className="flex-shrink-0 w-[300px] md:w-[320px] lg:w-[340px]">
              <div 
                className={`relative h-full rounded-2xl p-6 md:p-8 transition-all duration-300 ${
                  item.highlight 
                    ? 'bg-gradient-to-br from-fgs-primary/20 to-fgs-primary/5 border-2 border-fgs-primary shadow-lg shadow-fgs-primary/10' 
                    : 'bg-white/[0.02] border border-white/5 hover:border-fgs-primary/30'
                }`}
              >
                {item.highlight && (
                  <div className="absolute -top-3 left-6 px-3 py-1 bg-fgs-primary text-white text-xs font-bold rounded-full shadow-lg">
                    EXCLUSIF
                  </div>
                )}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${item.highlight ? 'bg-fgs-primary/20' : 'bg-white/5'}`}>
                  <item.icon className="w-7 h-7 text-fgs-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/50 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center justify-center gap-2 mt-4 text-fgs-text-muted text-xs">
        <ChevronLeft className="w-3 h-3" />
        <span>Glissez pour voir plus</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
};

export default HomePage;
